import React, { useCallback, useEffect } from "react";
import SubscriptionPage from "../../entities/SubscriptionPage";
import { subscriptionService } from "../../api";
import { useAlert } from "../../context/AlertContext";
import LoaderHelper from "../../utils/Loader/LoaderHelper";

const normalizeListResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.result)) return response.result;
  return [];
};

const Subscription = () => {
  const { showAlert } = useAlert();
  const [limits, setLimits] = React.useState({
    payslipLimit: "",
    employeeLimit: "",
  });
  const [initialLimits, setInitialLimits] = React.useState({
    payslipLimit: "",
    employeeLimit: "",
  });
  const [activeSubscriptionId, setActiveSubscriptionId] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);

  const fetchSubscriptionData = useCallback(async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const listResponse = await subscriptionService.list();

      const records = normalizeListResponse(listResponse);
      if (records.length) {
        const latest = [...records].sort(
          (a, b) =>
            new Date(b?.updatedAt || b?.createdAt || 0) -
            new Date(a?.updatedAt || a?.createdAt || 0)
        )[0];
        const nextLimits = {
          payslipLimit:
            latest?.payslipLimit === null || latest?.payslipLimit === undefined
              ? ""
              : String(latest?.payslipLimit),
          employeeLimit:
            latest?.employeeLimit === null || latest?.employeeLimit === undefined
              ? ""
              : String(latest?.employeeLimit),
        };
        setLimits(nextLimits);
        setInitialLimits(nextLimits);
        setActiveSubscriptionId(latest?._id ?? null);
      } else {
        const reset = { payslipLimit: "", employeeLimit: "" };
        setLimits(reset);
        setInitialLimits(reset);
        setActiveSubscriptionId(null);
      }
    } catch (error) {
      showAlert(
        error?.message || "Unable to load subscription information.",
        "error"
      );
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  const handleInputChange = (field, value) => {
    if (!/^\d*$/.test(value)) return;

    if (value === "") {
      setLimits((prev) => ({ ...prev, [field]: value }));
      return;
    }

    const numericValue = Number(value);
    if (numericValue < 1 || numericValue > 10000) {
      showAlert("Limits must be between 1 and 10,000.", "error");
      return;
    }

    setLimits((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (isSaving) return;

    const employeeLimit = Number(limits.employeeLimit);
    const payslipLimit = Number(limits.payslipLimit);

    if (
      Number.isNaN(employeeLimit) ||
      Number.isNaN(payslipLimit) ||
      employeeLimit < 1 ||
      employeeLimit > 10000 ||
      payslipLimit < 1 ||
      payslipLimit > 10000
    ) {
      showAlert("Limits must be between 1 and 10,000.", "error");
      return;
    }

    const payload = {
      employeeLimit,
      payslipLimit,
    };
    setIsSaving(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await subscriptionService.createOrUpdate(
        payload,
        activeSubscriptionId
      );

      if (result?.success || result?.status) {
        showAlert(
          result?.message || "Subscription limits saved successfully.",
          "success"
        );
        await fetchSubscriptionData();
      } else {
        showAlert(
          result?.message || "Unable to save subscription limits.",
          "error"
        );
      }
    } catch (error) {
      showAlert(
        error?.message || "Unable to save subscription limits.",
        "error"
      );
    } finally {
      setIsSaving(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const hasChanges =
    limits.payslipLimit !== initialLimits.payslipLimit ||
    limits.employeeLimit !== initialLimits.employeeLimit;

  const isSaveDisabled =
    isSaving ||
    limits.payslipLimit === "" ||
    limits.employeeLimit === "" ||
    !hasChanges;

  return (
    <SubscriptionPage
      limits={limits}
      isSaving={isSaving}
      onChange={handleInputChange}
      onSave={handleSave}
      isSaveDisabled={isSaveDisabled}
    />
  );
};

export default Subscription;